import type React from "react";
import { createContext, use, useCallback, useState } from "react";

import type { ErrorT } from "~/services/api-schemas";

import { invariant } from "@nephroflow/design-system/utils/invariant";
import { camelizeErrorSource } from "~/utils/errors";

export type FailureType =
  | "forbidden"
  | "not_found"
  | "method_not_allowed"
  | "content_too_large"
  | "unprocessable_content"
  | "internal_server_error"
  | "unhandled_error";
type HandlerType = "fallback_modal" | "form";

interface GlobalError {
  failureType: FailureType;
  handlerType: HandlerType;
  errors: ErrorT[];
}

interface GlobalErrorsContextT {
  error: GlobalError | null;
  setError: (arg: GlobalError | null) => void;
}

const GlobalErrorsContext = createContext<GlobalErrorsContextT | undefined>(undefined);

export function GlobalErrorsProvider({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<GlobalError | null>(null);

  return <GlobalErrorsContext value={{ error, setError }}>{children}</GlobalErrorsContext>;
}

export function useHandleGlobalErrors() {
  const globalErrorsContext = use(GlobalErrorsContext);

  invariant(globalErrorsContext, "The hook `useHandleGlobalErrors` must be used within a `GlobalErrorsProvider`.");

  const { setError } = globalErrorsContext;

  return useCallback(
    ({ error, handlerType = "fallback_modal" }: { handlerType?: HandlerType; error: unknown }) => {
      const response = error as Record<string, any>;
      if (import.meta.env.DEV && import.meta.env.MODE !== "test") {
        console.error(response);
      }

      const errors = response?.errors?.map(camelizeErrorSource);
      // 403 Forbidden
      // The HTTP 403 Forbidden response status code indicates that the server understands the request but refuses to
      // authorize it.
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/403
      if (response?.status === 403) {
        return setError({
          failureType: "forbidden",
          handlerType,
          errors,
        });
      }

      // 404 Not Found
      // The HTTP 404 Not Found response status code indicates that the server cannot find the requested resource. Links
      // that lead to a 404 page are often called broken or dead links and can be subject to link rot
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/404
      if (response?.status === 404) {
        return setError({ failureType: "not_found", handlerType: "fallback_modal", errors: [] });
      }

      // 405 Method Not Allowed
      // The HTTP 405 Method Not Allowed response status code indicates that the server knows the request method, but the
      // target resource doesn't support this method.
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/405
      if (response?.status === 405) {
        return setError({ failureType: "method_not_allowed", handlerType: "fallback_modal", errors: [] });
      }

      // 413 Content Too Large
      // The HTTP 413 Content Too Large response status code indicates that the request entity is larger than limits
      // defined by server; the server might close the connection or return a Retry-After header field.
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/413
      if (response?.status === 413) {
        return setError({ failureType: "content_too_large", handlerType: "fallback_modal", errors: [] });
      }

      // 422 Unprocessable Content
      // The HTTP 422 Unprocessable Content response status code indicates that the server understands the content type of
      // the request entity, and the syntax of the request entity is correct, but it was unable to process the contained
      // instructions.
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/422
      if (response?.status === 422) {
        return setError({
          failureType: "unprocessable_content",
          handlerType,
          errors,
        });
      }

      // 500 Internal Server Error
      // The HTTP 500 Internal Server Error server error response code indicates that the server encountered an unexpected
      // condition that prevented it from fulfilling the request.
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/500
      if (response?.status >= 500) {
        return setError({ failureType: "internal_server_error", handlerType: "fallback_modal", errors: [] });
      }

      return setError({
        failureType: "unhandled_error",
        handlerType: "fallback_modal",
        errors,
      });
    },
    [setError],
  );
}

export function useGetGlobalErrors() {
  const globalErrorsContext = use(GlobalErrorsContext);

  invariant(globalErrorsContext, "The hook `useGetGlobalErrors` must be used within a `GlobalErrorsProvider`.");

  return globalErrorsContext.error;
}

export function useResetGlobalErrors() {
  const globalErrorsContext = use(GlobalErrorsContext);

  invariant(globalErrorsContext, "The hook `useResetGlobalErrors` must be used within a `GlobalErrorsProvider`.");

  const { setError } = globalErrorsContext;

  return useCallback(() => {
    setError(null);
  }, [setError]);
}
